/**
 * SecureEmbed Management Portal
 *
 * Main application component: routing, layout, and session-managed authentication.
 *
 * The sidebar navigation is defined by the `navigation` array below (see the
 * Management Portal redesign plan, documentation/…Redesign…Plan.docx, for the
 * target grouped IA this is migrating toward). Elevated routes gate on the
 * Cognito 'Admins' group via ProtectedRoute, matching the backend handlers.
 */

import React, { useState, useEffect, useCallback, createContext, useContext, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  Settings,
  Bell,
  Menu,
  X,
  Shield,
  LogOut,
  User,
  ChevronDown,
  Loader2,
  Home,
  Sliders,
  PanelLeftClose,
  PanelLeft,
  HardDrive,
  Server,
  Users,
  Archive,
  FileText,
  Layout as LayoutIcon,
  DollarSign,
  ExternalLink,
  Key,
  Monitor as MonitorIcon,
  Activity,
  Rocket,
  Package,
  LifeBuoy,
  BookOpen,
} from 'lucide-react';

import { STORAGE_KEYS, AUTH_CONFIG } from './constants/config';
import { performLogout, AppErrorBoundary } from '@secureembed/shared-crm';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { EnvironmentProvider } from './contexts/EnvironmentContext';
import EnvironmentSelector from './components/EnvironmentSelector';

// Import pages
import Dashboard from './pages/Dashboard';
// DeploymentHub removed — /deploy redirects to /
import Monitor from './pages/Monitor';
// DataIngestion removed — replaced by IngestionWizard + IngestionJobs
import SettingsPage from './pages/Settings';
import Login from './pages/Login'
import Forbidden from './pages/Forbidden';
import NotFound from './pages/NotFound';
import ProfilePage from './pages/Profile';
import PreferencesPage from './pages/Preferences';
// IngestionMonitor deprecated — replaced by IngestionActivity
import IngestionActivity from './pages/IngestionActivity';
import IngestionControl from './pages/IngestionControl';
// CreateIngestionJob removed — replaced by IngestionWizard
import IngestionWizard from './pages/IngestionWizard';
import IngestionJobs from './pages/IngestionJobs';
import IngestionJobDetail from './pages/IngestionJobDetail';
import AemoIngestion from './pages/AemoIngestion';
// AemoDatastore, AerDatastore, BomDatastore removed — sub-pages consolidated into /datastores
import Organizations from './pages/Organizations';
import AccessUsers from './pages/AccessUsers';
import AccessControl from './pages/AccessControl';
import PortalUsersPage from './pages/PortalUsers';
import PricingConfiguration from './pages/PricingConfiguration';
import { VersionBadge } from '@secureembed/shared-crm';

// Lazy-loaded pages
const DatastoreBrowser = lazyWithRetry(() => import('./pages/DatastoreBrowser'), './pages/DatastoreBrowser');
const OrganisationsPage = lazyWithRetry(() => import('./components/organizations/OrganisationsPage'), './components/organizations/OrganisationsPage');
const OrganisationDetail = lazyWithRetry(() => import('./components/organizations/OrganisationDetail'), './components/organizations/OrganisationDetail');
const DataStoresPage = lazyWithRetry(() => import('./pages/DataStores'), './pages/DataStores');
const PlatformServices = lazyWithRetry(() => import('./pages/PlatformServices'), './pages/PlatformServices');
const ServiceDetail = lazyWithRetry(() => import('./pages/ServiceDetail'), './pages/ServiceDetail');
const BackupRestore = lazyWithRetry(() => import('./pages/BackupRestore'), './pages/BackupRestore');
const BackupCatalogue = lazyWithRetry(() => import('./pages/BackupCatalogue'), './pages/BackupCatalogue');
const VaultsDR = lazyWithRetry(() => import('./pages/VaultsDR'), './pages/VaultsDR');
const KeyManagement = lazyWithRetry(() => import('./pages/KeyManagement'), './pages/KeyManagement');
const Knowledge = lazyWithRetry(() => import('./pages/Knowledge'), './pages/Knowledge');
const ContactsList = lazyWithRetry(() => import('./pages/ContactsList'), './pages/ContactsList');
const ContactDetail = lazyWithRetry(() => import('./pages/ContactDetail'), './pages/ContactDetail');
const CRMDashboard = lazyWithRetry(() => import('./pages/CRMDashboard'), './pages/CRMDashboard');
const InteractionsPage = lazyWithRetry(() => import('./pages/InteractionsPage'), './pages/InteractionsPage');
const DocumentsPage = lazyWithRetry(() => import('./pages/DocumentsPage'), './pages/DocumentsPage');
const SharingPage = lazyWithRetry(() => import('./pages/SharingPage'), './pages/SharingPage');
const AccessControlDetail = lazyWithRetry(() => import('./pages/AccessControlDetail'), './pages/AccessControlDetail');
const Logs = lazyWithRetry(() => import('./pages/Logs'), './pages/Logs');
const Portals = lazyWithRetry(() => import('./pages/Portals'), './pages/Portals');
const PortalSettings = lazyWithRetry(() => import('./pages/PortalSettings'), './pages/PortalSettings');
const EnergyOrganisations = lazyWithRetry(() => import('./pages/EnergyOrganisations'), './pages/EnergyOrganisations');
const DeveloperVMs = lazyWithRetry(() => import('./pages/DeveloperVMs'), './pages/DeveloperVMs');
const DeveloperVMDetail = lazyWithRetry(() => import('./pages/DeveloperVMDetail'), './pages/DeveloperVMDetail');
// RT-18: EnvironmentControl is fully superseded (baselines/pull/promote live in
// EnvironmentDetail + ReleasesHub) — /environment-control now redirects to
// /environments. The page file is kept for history/reference only.
const EnvironmentsHub = lazyWithRetry(() => import('./pages/EnvironmentsHub'), './pages/EnvironmentsHub');
const EnvironmentDetail = lazyWithRetry(() => import('./pages/EnvironmentDetail'), './pages/EnvironmentDetail');
const ReleasesHub = lazyWithRetry(() => import('./pages/ReleasesHub'), './pages/ReleasesHub');
const PromotionPackages = lazyWithRetry(() => import('./pages/PromotionPackages'), './pages/PromotionPackages');
// W4: consolidated tabbed hubs (each renders one PageHeader + a tab bar + the
// embedded sibling pages). The old sibling routes are kept as deep-links.
const BackupsHub = lazyWithRetry(() => import('./pages/BackupsHub'), './pages/BackupsHub');
const IngestionHub = lazyWithRetry(() => import('./pages/IngestionHub'), './pages/IngestionHub');
const CostsPricingHub = lazyWithRetry(() => import('./pages/CostsPricingHub'), './pages/CostsPricingHub');
const UsersOrgsHub = lazyWithRetry(() => import('./pages/UsersOrgsHub'), './pages/UsersOrgsHub');
const SupportTickets = lazyWithRetry(() => import('./pages/SupportTickets'), './pages/SupportTickets');

// =============================================================================
// AUTH CONTEXT - Centralized authentication state management
// =============================================================================

interface AuthUser {
  email?: string;
  tenant_id?: string;
  role?: string;
  username?: string;
  groups?: string[];
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  login: (token: string, user?: AuthUser) => void;
  logout: () => void;
  checkAuth: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider component
const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AuthUser | null>(null);

  const parseGroupsFromToken = useCallback((token: string): string[] => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return [];
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      return Array.isArray(payload['cognito:groups']) ? payload['cognito:groups'] : [];
    } catch {
      return [];
    }
  }, []);

  // C2-partial: Check token expiry with proper base64url decoding
  const isTokenValid = useCallback((token: string): boolean => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        // For demo tokens or non-JWT, assume valid
        return token.startsWith('demo-token-') || token.length > 0;
      }
      // C2: Proper base64url decoding for JWT payload
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        // Token expired - clear all tokens
        localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }, []);

  // Check authentication status
  const checkAuth = useCallback((): boolean => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!token) {
      return false;
    }
    return isTokenValid(token);
  }, [isTokenValid]);

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = () => {
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      const storedUser = localStorage.getItem(STORAGE_KEYS.USER);

      if (token && isTokenValid(token)) {
        setIsAuthenticated(true);
        if (storedUser) {
          try {
            const parsed = JSON.parse(storedUser) as AuthUser;
            if (!parsed.groups) parsed.groups = parseGroupsFromToken(token);
            setUser(parsed);
          } catch {
            setUser({ email: AUTH_CONFIG.DEFAULT_USER_EMAIL, groups: parseGroupsFromToken(token) });
          }
        }
      } else {
        // Clear invalid tokens
        localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.ID_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.USER);
        setIsAuthenticated(false);
        setUser(null);
      }
      setIsLoading(false);
    };

    initAuth();

    // Re-check auth periodically
    const interval = setInterval(() => {
      if (!checkAuth()) {
        setIsAuthenticated(false);
        setUser(null);
      }
    }, AUTH_CONFIG.CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isTokenValid, checkAuth]);

  const login = useCallback((token: string, userData?: AuthUser) => {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
    const enriched: AuthUser = {
      ...userData,
      groups: userData?.groups ?? parseGroupsFromToken(token),
    };
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(enriched));
    setUser(enriched);
    setIsAuthenticated(true);
  }, [parseGroupsFromToken]);

  const logout = useCallback(() => {
    performLogout({
      storageKeys: [
        STORAGE_KEYS.AUTH_TOKEN,
        STORAGE_KEYS.ID_TOKEN,
        STORAGE_KEYS.REFRESH_TOKEN,
        STORAGE_KEYS.USER,
      ],
      onClear: () => {
        // RT-3: drop all cached query data on logout so the next user in this tab
        // never sees the previous user's data (session-bleed hardening, #2256).
        queryClient.clear();
        setIsAuthenticated(false);
        setUser(null);
      },
    });
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// =============================================================================
// REACT QUERY CLIENT
// =============================================================================

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// =============================================================================
// NAVIGATION - Simplified navigation bar
// Task 1.3: [Home] [Operations] [Data Ingestion] [Analytics]
// Settings, Profile, Preferences, Sign Out moved to User dropdown
// =============================================================================

interface NavLeaf {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface NavGroup {
  // null section = top-level items rendered without a header (e.g. Overview)
  section: string | null;
  items: NavLeaf[];
}

// W1a: grouped information architecture (see the Management Portal redesign plan,
// documentation/…Redesign…Plan.docx §4). Every routed page is reachable from the
// sidebar — this closes the CRITICAL findings that Monitor and the entire CRM
// workspace were unreachable except by typed URL. Pages that the later waves fold
// into tabbed hubs (Ingestion, Backups, Users, Costs+Pricing, CRM) are listed
// individually here for now; W4 consolidates them.
export const navGroups: NavGroup[] = [
  { section: null, items: [
    { name: 'Overview', href: '/', icon: Home },
  ] },
  { section: 'Observe', items: [
    { name: 'Monitor', href: '/monitor', icon: MonitorIcon },
    { name: 'Logs', href: '/logs', icon: FileText },
    { name: 'Support Tickets', href: '/support', icon: LifeBuoy },
    { name: 'Ingestion Activity', href: '/monitor/activity', icon: Activity },
  ] },
  { section: 'Data', items: [
    { name: 'Data Stores', href: '/datastores', icon: HardDrive },
    { name: 'Ingestion', href: '/ingestion', icon: Activity },
    { name: 'Backups', href: '/backups', icon: Archive },
  ] },
  { section: 'Environments', items: [
    { name: 'Environments', href: '/environments', icon: Server },
    { name: 'Releases', href: '/releases', icon: Rocket },
    { name: 'Promotion Packages', href: '/promotion-packages', icon: Package },
  ] },
  { section: 'Platform', items: [
    { name: 'Platform Services', href: '/services', icon: Server },
    { name: 'Portals', href: '/portals', icon: LayoutIcon },
    { name: 'Developer VMs', href: '/developer-vms', icon: MonitorIcon },
    { name: 'Knowledge', href: '/knowledge', icon: BookOpen },
  ] },
  { section: 'Access & Security', items: [
    { name: 'Users & Orgs', href: '/users-orgs', icon: Users },
    { name: 'Access Control', href: '/access', icon: Shield },
    { name: 'Encryption Keys', href: '/key-management', icon: Key },
  ] },
  { section: 'Billing', items: [
    { name: 'Costs & Pricing', href: '/billing', icon: DollarSign },
  ] },
  { section: 'CRM', items: [
    { name: 'CRM Workspace', href: '/crm', icon: Users },
  ] },
];

// Flat list of every nav destination — used by the route-manifest test to assert
// each sidebar link resolves to a real route.
export const allNavHrefs: string[] = navGroups.flatMap((g) => g.items.map((i) => i.href));

// Sidebar component - collapsible navigation with localStorage persistence
const Sidebar: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}> = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const location = useLocation();

  // Check if a path is active
  const isPathActive = (href: string) => {
    if (href.includes('?')) {
      const [basePath, queryString] = href.split('?');
      // Must match both the base path AND the query string
      return location.pathname === basePath && location.search.includes(queryString);
    }
    return location.pathname === href;
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-screen min-h-screen bg-iflow-700 text-white transform transition-all duration-200
        lg:translate-x-0 lg:static lg:z-0 lg:h-auto lg:min-h-full flex flex-col
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        ${isCollapsed ? 'w-16' : 'w-64'}
      `}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
          <div className="flex items-start space-x-2">
            <Shield className="w-8 h-8 flex-shrink-0" />
            {!isCollapsed && (
              <div className="flex flex-col leading-tight mt-0.5">
                <span className="font-bold text-lg leading-tight">SecureEmbed</span>
                <span className="text-[10px] text-white/50 uppercase tracking-wider">Platform Management</span>
                <VersionBadge portal="dashboard" collapsed={false} />
              </div>
            )}
          </div>
          <button onClick={onClose} className="lg:hidden">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Collapse toggle button */}
        <div className="px-3 py-2">
          <button
            onClick={onToggleCollapse}
            className={`w-full flex items-center px-3 py-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-all ${isCollapsed ? 'justify-center' : ''}`}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <PanelLeft className="w-5 h-5" />
            ) : (
              <PanelLeftClose className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Navigation — grouped sections (W1a). Section headers group the items;
            when the sidebar is collapsed the headers are hidden and only icons
            show. Active state matches the exact path (and nested sub-paths). */}
        <nav className="px-2 flex-1 overflow-y-auto">
          {navGroups.map((group, gi) => (
            <div key={group.section ?? `group-${gi}`} className="mb-1">
              {group.section && !isCollapsed && (
                <div className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">
                  {group.section}
                </div>
              )}
              {group.section && isCollapsed && gi > 0 && (
                <div className="mx-3 my-2 border-t border-white/10" />
              )}
              {group.items.map((item) => {
                const isActive = isPathActive(item.href)
                  || (item.href !== '/' && location.pathname.startsWith(item.href + '/'));
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={onClose}
                    title={isCollapsed ? item.name : undefined}
                    className={`
                      flex items-center px-3 py-2 rounded-lg mb-0.5 transition-colors
                      ${isCollapsed ? 'justify-center' : ''}
                      ${isActive
                        ? 'bg-white/20 text-white font-medium'
                        : 'text-white/70 hover:bg-white/10 hover:text-white'}
                    `}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    {!isCollapsed && <span className="truncate ml-3 text-sm">{item.name}</span>}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* External portal link */}
        {!isCollapsed && (
          <div className="flex-shrink-0 px-2 pb-2">
            <div className="border-t border-white/10 pt-2">
              <a
                href="https://d2k5ne4hoaiwmb.cloudfront.net"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-3 py-2.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors text-sm"
              >
                <Users className="w-4 h-4 flex-shrink-0" />
                <span className="truncate ml-3">Stakeholder Portal</span>
                <ExternalLink className="w-3.5 h-3.5 ml-auto flex-shrink-0" />
              </a>
            </div>
          </div>
        )}
        {isCollapsed && (
          <div className="flex-shrink-0 px-2 pb-2">
            <div className="border-t border-white/10 pt-2">
              <a
                href="https://d2k5ne4hoaiwmb.cloudfront.net"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center px-3 py-2.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                title="Stakeholder Portal"
              >
                <ExternalLink className="w-5 h-5" />
              </a>
            </div>
          </div>
        )}

        <div className="flex-shrink-0 h-4" />
      </aside>
    </>
  );
};

// Header component with User Menu dropdown
const Header: React.FC<{ onMenuClick: () => void }> = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-user-menu]')) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setUserMenuOpen(false);
  }, [location.pathname]);

  const userInitial = user?.email?.charAt(0).toUpperCase() || user?.username?.charAt(0).toUpperCase() || 'U';
  const displayName = user?.email || user?.username || 'User';

  return (
    <header className="h-16 bg-gradient-to-r from-white via-gray-50 to-white border-b border-gray-200 shadow-sm flex items-center justify-between px-4 lg:px-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
        >
          <Menu className="w-6 h-6 text-gray-600" />
        </button>
      </div>

      <div className="flex items-center space-x-3">
        {/* Global environment selector (W1b) */}
        <EnvironmentSelector />

        {/* Notifications — the unread indicator is intentionally NOT rendered
            until it is driven by the real alerts feed (Overview alerts, W3). A
            permanently-lit red dot with no data source is a false signal. */}
        <button
          type="button"
          aria-label="Notifications"
          title="Notifications"
          className="relative p-2 rounded-lg hover:bg-gray-100"
        >
          <Bell className="w-5 h-5 text-gray-600" />
        </button>

        {/* User Menu Dropdown */}
        <div className="relative" data-user-menu>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center space-x-2 p-1 pr-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-iflow-700 flex items-center justify-center text-white font-medium">
              {userInitial}
            </div>
            <span className="hidden sm:inline text-sm font-medium text-gray-700">{user?.username === 'admin' ? 'Admin' : 'User'}</span>
            <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu */}
          {userMenuOpen && (
            <div className="absolute right-0 mt-2 w-64 bg-gray-100 rounded-xl shadow-lg border border-gray-200 py-2 z-50">
              {/* User Info */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-iflow-700 flex items-center justify-center text-white font-medium">
                    {userInitial}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.username === 'admin' ? 'Administrator' : (user?.role || 'User')}</p>
                  </div>
                </div>
              </div>

              {/* Menu Items - Profile, Settings, Preferences */}
              <div className="py-1">
                <Link
                  to="/profile"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <User className="w-4 h-4 text-gray-500" />
                  <span>Profile</span>
                </Link>
                <Link
                  to="/settings"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Settings className="w-4 h-4 text-gray-500" />
                  <span>Settings</span>
                </Link>
                <Link
                  to="/preferences"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Sliders className="w-4 h-4 text-gray-500" />
                  <span>Preferences</span>
                </Link>
              </div>

              {/* Sign Out */}
              <div className="border-t border-gray-100 py-1">
                <button
                  onClick={logout}
                  className="flex items-center space-x-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

// Main layout component with collapsible sidebar
const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Initialize from localStorage
    const saved = localStorage.getItem(STORAGE_KEYS.SIDEBAR_COLLAPSED);
    return saved === 'true';
  });

  const toggleCollapse = useCallback(() => {
    setSidebarCollapsed(prev => {
      const newValue = !prev;
      localStorage.setItem(STORAGE_KEYS.SIDEBAR_COLLAPSED, String(newValue));
      return newValue;
    });
  }, []);

  return (
    <div className="min-h-screen bg-iflow-50 flex">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={toggleCollapse}
      />

      <div className="flex-1 flex flex-col min-h-screen">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

// =============================================================================
// PROTECTED ROUTE - Enhanced with proper auth checking and loading states
// =============================================================================

const ProtectedRoute: React.FC<{ children: React.ReactNode; requiredGroup?: string }> = ({
  children,
  requiredGroup,
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-iflow-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-iflow-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // W0-2: elevated routes gate on the Cognito group that actually exists and is
  // populated — 'Admins' — which is exactly what the backend handlers enforce
  // (e.g. demo-env require_auth(require_admin=True) checks 'Admins'). The old code
  // gated on a 'platform-admin' group nobody is in and then bypassed it for
  // 'Admins', so the label was dead and the real gate was implicit. Checking the
  // required group directly (no blanket bypass) keeps front-end and back-end in
  // lockstep and lets a genuine second tier (a real platform-admin group for T2 /
  // prod destructive actions) be enforced correctly when it lands in W2/W3.
  if (requiredGroup && !user?.groups?.includes(requiredGroup)) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
};

// =============================================================================
// APP COMPONENT - Main application with AuthProvider
// =============================================================================

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <EnvironmentProvider>
          <BrowserRouter>
          <Routes>
            {/* Public route - Login (only accessible when NOT authenticated) */}
            <Route path="/login" element={<Login />} />
            <Route path="/forbidden" element={<Forbidden />} />

            {/* Protected routes - ALL require authentication */}
            <Route path="/" element={
              <ProtectedRoute>
                <Layout><Dashboard /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <Layout><SettingsPage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/monitor" element={
              <ProtectedRoute>
                <Layout><Monitor /></Layout>
              </ProtectedRoute>
            } />
            {/* IngestionMonitor deprecated — redirect to IngestionActivity */}
            <Route path="/monitor/ingestion" element={
              <Navigate to="/monitor/activity" replace />
            } />
            <Route path="/monitor/activity" element={
              <ProtectedRoute>
                <Layout><IngestionActivity /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/ingestion/create" element={
              <ProtectedRoute>
                <Layout><IngestionWizard /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/ingestion/jobs/:jobId" element={
              <ProtectedRoute>
                <Layout><IngestionJobDetail /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/ingestion/jobs" element={
              <ProtectedRoute>
                <Layout><IngestionJobs /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/ingestion/control" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><IngestionControl /></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/ingestion/wizard/:jobId" element={
              <ProtectedRoute>
                <Layout><IngestionWizard /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/ingestion/wizard" element={
              <ProtectedRoute>
                <Layout><IngestionWizard /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/datastores/:name/browse" element={
              <ProtectedRoute>
                <Layout>
                  <AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}>
                    <DatastoreBrowser />
                  </Suspense></AppErrorBoundary>
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/datastores" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><DataStoresPage /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/services" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><PlatformServices /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/services/:componentId" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ServiceDetail /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/key-management" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><KeyManagement /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* ADR-013 — internal design documentation and first-party source.
                Admins only, and the Lambda re-checks the group itself. */}
            <Route path="/knowledge" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><Knowledge /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/backup" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><BackupRestore /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/backup/catalogue" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><BackupCatalogue /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/backup/vaults" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><VaultsDR /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* W4: consolidated tabbed hub routes (ADDITIVE — the sibling routes
                above/below are kept intact as deep-links + route-manifest anchors).
                Guard matches the strictest sibling: Backups + Ingestion expose
                admin-gated surfaces → Admins; Billing siblings are auth-only. */}
            <Route path="/backups" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><BackupsHub /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/ingestion" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><IngestionHub /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Story #4869: Admins-only. The Costs tab now shows the WHOLE
                organisation's spend read from the payer account, including Audit
                and Log Archive, which leaks org topology and relative activity
                levels. /users and /organizations already gate on Admins; billing
                was auth-only, so a Monitors user could read it. */}
            <Route path="/billing" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><CostsPricingHub /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/access" element={
              <ProtectedRoute>
                <Layout><AccessControl /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/portal-users" element={
              <ProtectedRoute>
                <Layout><PortalUsersPage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/access/organizations" element={
              <ProtectedRoute>
                <Layout><Organizations /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/organisations" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" /></div>}><OrganisationsPage /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/organisations/:id" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" /></div>}><OrganisationDetail /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/access/users" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AccessUsers /></Layout>
              </ProtectedRoute>
            } />
            {/* Polish wave: consolidated Users & Organisations hub. Guard matches
                the strictest sibling (/access/users → Admins); the sibling routes
                above/below stay intact as deep-links. */}
            <Route path="/users-orgs" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><UsersOrgsHub /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/aemo" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AemoIngestion /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/energy-organisations" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnergyOrganisations /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/crm" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><CRMDashboard /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/crm/interactions" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><InteractionsPage /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/crm/documents" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><DocumentsPage /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* Network/Connections removed — cross-instance feature violates the
                Management CRM isolation requirement. Redirect any stale link. */}
            <Route path="/crm/network" element={<Navigate to="/crm" replace />} />
            <Route path="/crm/sharing" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><SharingPage /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/contacts" element={<Navigate to="/crm" replace />} />
            <Route path="/contacts/people" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ContactsList /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/contacts/people/:id" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ContactDetail /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/access/:section" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><AccessControlDetail /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/logs" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><Logs /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/support" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><SupportTickets /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/portals" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><Portals /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/portals/:portalId/settings" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><PortalSettings /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/developer-vms" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><DeveloperVMs /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/developer-vms/:vmId" element={
              <ProtectedRoute>
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><DeveloperVMDetail /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/environments" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentsHub /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/environments/:env" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><EnvironmentDetail /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/releases" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><ReleasesHub /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            <Route path="/promotion-packages" element={
              <ProtectedRoute requiredGroup="Admins">
                <Layout><AppErrorBoundary><Suspense fallback={<div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>}><PromotionPackages /></Suspense></AppErrorBoundary></Layout>
              </ProtectedRoute>
            } />
            {/* RT-18: legacy Environment Control page is superseded by
                EnvironmentDetail + ReleasesHub — redirect, keep the file. */}
            <Route path="/environment-control" element={<Navigate to="/environments" replace />} />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Layout><ProfilePage /></Layout>
              </ProtectedRoute>
            } />
            <Route path="/preferences" element={
              <ProtectedRoute>
                <Layout><PreferencesPage /></Layout>
              </ProtectedRoute>
            } />
            {/* Story #4869: the /costs route was REMOVED. Nothing in the app
                ever linked to it -- navigation goes to /billing, which renders the
                same CostsPage embedded in CostsPricingHub -- and it carried only a
                bare ProtectedRoute. Gating /billing while leaving this open would
                have left a second, ungated door onto org-wide billing data, and
                once the backend Admins gate landed a Monitors user reaching it
                would have seen a raw error banner instead of /forbidden. */}
            <Route path="/pricing" element={
              <ProtectedRoute>
                <Layout><PricingConfiguration /></Layout>
              </ProtectedRoute>
            } />
            {/* Legacy route redirects */}
            <Route path="/deploy" element={<Navigate to="/" replace />} />
            <Route path="/infrastructure" element={<Navigate to="/" replace />} />
            <Route path="/operations" element={<Navigate to="/" replace />} />

            {/* Catch-all: a real 404 (W1a / RT-10) instead of a silent redirect
                to '/', so a dropped/broken route is visible rather than masked. */}
            <Route path="*" element={
              <ProtectedRoute>
                <Layout><NotFound /></Layout>
              </ProtectedRoute>
            } />
          </Routes>
          </BrowserRouter>
        </EnvironmentProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

// Export useAuth hook for use in other components
export { useAuth };
export default App;
